/**
 * Mixpanel event names from the catalog (docs/mixpanel/MIXPANEL_EVENTS.md, section 3).
 * Always track through these constants so names never drift from the catalog.
 */
export const MIXPANEL_EVENTS = {
  LOGIN: 'DD Login',
  LOGIN_FAILED: 'DD Login - failed',
  LOGOUT: 'DD Logout',
  APP_PAGE_VIEW: 'DD App - page view',
  HEADER_NAVIGATION: 'DD Header - navigation',
  SIDEBAR_OPEN: 'DD Sidebar - open',
  SIDEBAR_CREATE: 'DD Sidebar - create',
  SIDEBAR_RENAME: 'DD Sidebar - rename',
  SIDEBAR_DELETE: 'DD Sidebar - delete',
  SIDEBAR_BULK_DELETE: 'DD Sidebar - bulk delete',
  DATA_DICTIONARY_OPEN_TABLE: 'DD Data dictionary - open table',
  DATA_DICTIONARY_TAB: 'DD Data dictionary - tab',
  DATA_DICTIONARY_EXPORT: 'DD Data dictionary - export',
  DATA_DICTIONARY_BULK_IMPORT: 'DD Data dictionary - bulk import',
  DICTIONARY_DETAIL_IMPORT: 'DD Dictionary detail - import',
  DICTIONARY_DETAIL_FIELD_CREATE: 'DD Dictionary detail - field create',
  DICTIONARY_DETAIL_FIELD_UPDATE: 'DD Dictionary detail - field update',
  DICTIONARY_DETAIL_FIELD_DELETE: 'DD Dictionary detail - field delete',
  DICTIONARY_DETAIL_DOWNLOAD_SAMPLE: 'DD Dictionary detail - download sample',
  MASTER_TABLES_DATA_TYPE: 'DD Master tables - data type',
  DATA_DICTIONARY_SEARCH: 'DD Data dictionary - search',
  DICTIONARY_DETAIL_DESCRIPTION_SAVE: 'DD Dictionary detail - description save',
  SIDEBAR_MOVE: 'DD Sidebar - move',
  ACCESS_DENIED: 'DD Access - denied',
} as const;

export type MixpanelEventName = (typeof MIXPANEL_EVENTS)[keyof typeof MIXPANEL_EVENTS];

export type MixpanelPageName =
  | 'data dictionary'
  | 'dictionary detail'
  | 'master tables'
  | 'logs'
  | 'unauthorized';

export type MixpanelItemType = 'folder' | 'table';

/** Normalizes a value to the catalog rules: lower case, no "_". */
export function toMixpanelValue(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replaceAll('_', ' ');
}

/**
 * Maps an app URL (router.url / urlAfterRedirects) to a catalog pageName.
 * Returns null for routes outside the catalog (login, SSO callback).
 */
export function pageNameFromUrl(url: string | null | undefined): MixpanelPageName | null {
  const path = (url ?? '').split(/[?#]/)[0];
  const segment = path.split('/').find(Boolean) ?? '';
  switch (segment) {
    case '':
      return 'data dictionary';
    case 'dictionary':
      return 'dictionary detail';
    case 'master-tables':
    case 'tablas-maestras':
      return 'master tables';
    case 'logs':
      return 'logs';
    case 'unauthorized':
      return 'unauthorized';
    default:
      return null;
  }
}

/** Parses a numeric id from a route/query param; undefined when missing or not numeric. */
export function toMixpanelId(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
}
